import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ModalService } from '../../core/services/modal.service';
import { UtilitiesService } from '../../core/services/utilities.service';
import { UserService } from '../../core/services/data/user.service';
import { AlertsService } from '../../core/services/alerts.service';
import { SettingsService } from '../../core/services/data/settings.service';
import { OrderService } from '../../core/services/data/order.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { IOrder } from '../../types/datatypes';

const DEFAULT_HOME_LINK = '/features';

@Component({
	// eslint-disable-next-line @angular-eslint/no-host-metadata-property
	host: {
		'(document:click)': 'onClick($event)',
	},
	selector: 'app-top-bar',
	templateUrl: './top-bar.component.html',
	styleUrls: ['./top-bar.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TopBarComponent implements OnInit, OnDestroy {
	public authorSide = true;

	public development;

	public showOps;

	public title: String;

	public user;

	public author;

	public alerts;

	public alertCount = 0;

	public noAlerts = true;

	public notificationsVisible = false;

	public orderItemCount = undefined;

	public homeRedirectLink = DEFAULT_HOME_LINK;

	public alerts$;

	public author$;

	public order$;

	public cartVisibility$;

	public settings$;

	public cartVisible = false;

	public refresh = true;

	public show = false;

	public cart: IOrder;

	@Input() authorDetached = false;

	@Input() authorSet;

	private refresh$;

	private subscriptions$ = new Subscription();

	constructor(
		private router: Router,
		private ms: ModalService,
		public uss: UserService,
		private us: UtilitiesService,
		private as: AlertsService,
		private ss: SettingsService,
		private os: OrderService,
		private analyticsService: AnalyticsService,
	) {}

	get isEmulated() {
		return this.uss.isEmulated;
	}

	ngOnInit() {
		this.os.fetchOrder();

		this.ss.getLoginSettings();

		this.order$ = this.os.orderStore$.subscribe((data: any) => {
			this.cart = data;
			this.orderItemCount = data?.items?.length;
		});

		this.refresh$ = this.os.orderRefresh.subscribe((orderId) => {
			if (orderId === this.cart._id) {
				this.os.refreshCart();
			}
		});

		this.cartVisibility$ = this.os.cartVisibleStatus$.subscribe((isCartOpen) => {
			this.cartVisible = !!isCartOpen;
		});
		if (this.authorSet) {
			this.author = this.authorSet;
		}

		this.subscriptions$.add(this.uss.user$.subscribe((user: any) => {
			this.user = user;
			this.showOps = this.uss.isAdminOrSuperAdmin();
			if (this.user && this.user.authorLink) {
				this.homeRedirectLink = `/author-dashboard/${this.user.authorLink}/home`;
			}
		}));

		this.author$ = this.uss.author$.pipe(
			tap((author: any) => {
			  this.author = author;
			  this.as.getBooks(author._id);
			}),
			switchMap(() => this.as.alerts),
		  ).subscribe((alerts) => {
			this.alerts = alerts;
			this.alertCount = Object.values(alerts)
			  .filter((alert) => alert.length > 0 && this.author.alerts[alert.type])
			  .length;
			this.noAlerts = this.alertCount === 0;
		  });
	}

	ngOnDestroy() {
		this.order$.unsubscribe();
		this.cartVisibility$.unsubscribe();
		this.author$.unsubscribe();
		this.refresh$?.unsubscribe();
		this.subscriptions$.unsubscribe();
	}

	onClick(event) {
		if (!event.target.classList.contains('menuDrop')) {
			this.show = false;
		}
	}

	goToForgotPassword() {
		this.router.navigate(['/forgot-password']).then();
	}

	goToSupport() {
		(window as any).open('https://support.writtenwordmedia.com/category/hvmn7oxmjs-written-word-media', '_blank');
	}

	toggleNotifications() {
		this.notificationsVisible = !this.notificationsVisible;
	}

	login() {
		this.us.saveRoute(window.location.pathname);

		this.router.navigate(['/login']);
	}

	signUp() {
		this.us.saveRoute(window.location.pathname);
		this.router.navigate(['/signup']);
	}

	handleCartClick() {
		this.handleCartAnalytics();
		return this.cartVisible ? this.os.setCartNotVisible() : this.os.setCartVisible();
	}

	handleCartAnalytics() {
		if (this.cartVisible || !this.cart?.items || !this.cart.items.length) {
			return;
		}
		this.analyticsService.clearEcommerce();

		let analyticsCartValue = 0;
		const analyticsCartItems = this.analyticsService.generateCartItemsEcommerceObjects(this.cart.items, (item, ecommItem) => {
			if ('paymentAmount' in item.item) {
				analyticsCartValue += item.item.paymentAmount;
			}
			return ecommItem;
		});

		this.analyticsService.trackViewCart(analyticsCartValue, analyticsCartItems);
	}

	isGoldMember() {
		return this.uss.isGoldMember();
	}

	isPlatinumMember() {
		return this.uss.isPlatinumMember();
	}

	roleName() {
		const role = this.author?.memberRole?.roleName;
		if (role) {
			return role;
		}

		return '';
	}

	roleNameUppercase() {
		const role = this.roleName();
		return role?.toUpperCase();
	}
}
